/**
 * Compatibility wrapper to match the old TooltipInternal API.
 * This allows gradual migration from the old design-system tooltip.
 *
 * Old API: <TooltipInternal title="text">{children}</TooltipInternal>
 * New API: <Tooltip content="text">{children}</Tooltip>
 *
 * This compat layer bridges the difference.
 */

import type { ReactElement, ReactNode } from 'react'

import { Tooltip } from './tooltip'

type TooltipCompatProps = {
    children: ReactElement
    /** Content to display in the tooltip (called 'title' for API compatibility) */
    title: ReactNode
    /** Controlled visibility (optional) */
    visible?: boolean
    /** Callback when visibility changes */
    onVisibleChange?: (visible: boolean) => void
}

/**
 * Compatibility wrapper for migrating from TooltipInternal.
 * Uses the same API as TooltipInternal but renders the new Tailwind-based tooltip.
 */
export const TooltipCompat = ({
    children,
    title,
    visible,
    onVisibleChange: _onVisibleChangeIgnored,
}: TooltipCompatProps) => (
    <Tooltip content={title} visible={visible}>
        {children}
    </Tooltip>
)

type ConditionalWrapWithTooltipProps = {
    visible?: boolean
    tooltip?: string
    children: ReactElement
}

/**
 * Compatibility wrapper for ConditionalWrapWithTooltip.
 * Conditionally wraps children with tooltip if tooltip prop is provided.
 */
export const ConditionalWrapWithTooltipCompat = ({ tooltip, children, visible }: ConditionalWrapWithTooltipProps) => {
    if (!tooltip) {
        return children
    }

    return (
        <Tooltip content={tooltip} visible={visible}>
            {children}
        </Tooltip>
    )
}

/** @public */
export const tooltipInternalDelay = 500
